/**
 * HelpCenterPage — /help: search, the Ask Resida Help entry, and the articles by topic (BR-13, BR-14).
 * Reads are available to every signed-in landlord regardless of plan (BR-9).
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/layout/index';
import { CategoryCards } from '../components/help/CategoryCards';
import { useHelp } from '../components/help/HelpProvider';
import { HelpSearchField, SearchResults } from '../components/help/SearchResults';

export function HelpCenterPage() {
  const navigate = useNavigate();
  const { chatDisabled } = useHelp();
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const askChat = chatDisabled ? undefined : () => navigate('/help/chat');

  return (
    <div className="mx-auto flex max-w-[960px] flex-col gap-6 px-[var(--page-padding-x)] pb-6" data-testid="help-center-page">
      <PageHeader title="Help center" subtitle="How-tos and answers for using Resida." subtitleWrap className="mb-0" />

      <HelpSearchField
        value={query}
        onChange={setQuery}
        placeholder="Search help, e.g. “add a tenant”"
        large
        data-testid="help-center-search"
      />

      {!chatDisabled && (
        <section
          aria-labelledby="help-center-ask-heading"
          className="flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary/5 p-[var(--card-padding)] sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex flex-col gap-1">
            <h2 id="help-center-ask-heading" className="text-sm font-semibold text-foreground">
              Ask Resida Help
            </h2>
            <p className="text-sm text-muted-foreground">
              Get an answer in plain language, with the help articles it came from.
            </p>
          </div>
          <Button type="button" onClick={askChat} data-testid="help-center-ask">
            Ask a question
          </Button>
        </section>
      )}

      {searching ? (
        <SearchResults query={query} onAsk={askChat} onContactSupport={() => navigate('/support')} />
      ) : (
        <CategoryCards variant="grid" />
      )}

      <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
        Still stuck?
        <Link
          to="/support"
          data-testid="help-center-contact"
          className="inline-flex min-h-[44px] items-center font-semibold text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Contact support
        </Link>
      </p>
    </div>
  );
}
